import { ToolBase, ToolPointer } from './tool';
import { Vec3, vec } from '../core/vec';

/**
 * Skalieren (S): Auswahl mit 8 Eckgriffen; Ziehen skaliert gleichmäßig
 * vom gegenüberliegenden Eckpunkt aus. Zahl tippen = Faktor (z. B. "2" oder "0,5").
 */
export class ScaleTool extends ToolBase {
  readonly id = 'scale';
  readonly label = 'Skalieren';
  readonly shortcut = 'S';
  readonly cursor = 'nwse-resize';
  readonly hint = 'Eckgriff ziehen = gleichmäßig skalieren · Zahl tippen = Faktor · Strg = vom Zentrum';

  private gripIndex: number | null = null;
  private corners: Vec3[] = [];
  private originals: { id: number; p: Vec3 }[] = [];
  private active = false;
  private center = vec();
  private currentFactor = 1;
  private anchorCorner = 0;
  /** Beim Greifen festgehaltener Anker und Bildschirm-Bezugslänge Griff↔Anker. */
  private anchorPoint: Vec3 | null = null;
  private baseLen = 1;

  enter(): void {
    this.active = false;
    this.gripIndex = null;
    this.originals = [];
    this.currentFactor = 1;
    this.computeCorners();
    if (this.ctx.selection.isEmpty) {
      this.ctx.ui.setStatus('Erst etwas auswählen (Leertaste), dann skalieren');
    }
  }

  exit(): void {
    this.ctx.preview.clear();
    this.ctx.requestUpdate();
  }

  private computeCorners(): void {
    const ids = this.ctx.selection.vertexIds(this.ctx.model);
    if (ids.size === 0) { this.corners = []; return; }
    let minX = Infinity, minY = Infinity, minZ = Infinity, maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    for (const id of ids) {
      const p = this.ctx.model.vertexPosition(id);
      minX = Math.min(minX, p.x); minY = Math.min(minY, p.y); minZ = Math.min(minZ, p.z);
      maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y); maxZ = Math.max(maxZ, p.z);
    }
    this.center = vec((minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2);
    // Reihenfolge: (x,y,z) Bits — Index Bit0=x-, Bit1=y-, Bit2=z- min/max
    this.corners = [
      vec(minX, minY, minZ), vec(maxX, minY, minZ),
      vec(minX, maxY, minZ), vec(maxX, maxY, minZ),
      vec(minX, minY, maxZ), vec(maxX, minY, maxZ),
      vec(minX, maxY, maxZ), vec(maxX, maxY, maxZ)
    ];
  }

  private captureSelection(): boolean {
    const ids = this.ctx.selection.vertexIds(this.ctx.model);
    if (ids.size === 0) return false;
    this.originals = [...ids].map((id) => ({ id, p: this.ctx.model.vertexPosition(id) }));
    return true;
  }

  private gripScreenPositions(): { x: number; y: number }[] {
    return this.corners.map((c) => this.ctx.camera.projectToScreen(c));
  }

  private drawHandles(): void {
    if (this.corners.length === 0) return;
    for (const c of this.corners) {
      this.ctx.preview.dot(c, 0xffffff, 9);
      this.ctx.preview.dot(c, 0x2f6fe0, 6);
    }
  }

  pointerMove(e: ToolPointer): void {
    this.ctx.preview.clear();
    if (!this.active || this.gripIndex === null || !this.anchorPoint) {
      this.computeCorners();
      this.drawHandles();
      this.ctx.requestUpdate();
      return;
    }
    // Faktor aus beim Greifen eingefrorenem Anker + Bezugslänge (nicht aus der schon skalierten Geometrie)
    const screenAnchor = this.ctx.camera.projectToScreen(this.anchorPoint);
    const curLen = Math.hypot(e.x - screenAnchor.x, e.y - screenAnchor.y);
    this.currentFactor = Math.max(0.01, curLen / this.baseLen);
    this.applyScale(this.currentFactor);
    this.ctx.ui.setStatus(`Faktor: ${Math.round(this.currentFactor * 100) / 100} · Zahl tippen = Faktor`);
    this.computeCorners();
    this.drawHandles();
    this.ctx.requestUpdate();
  }

  private applyScale(factor: number): void {
    const anchor = this.anchor();
    for (const o of this.originals) {
      const v = this.ctx.model.vertices.get(o.id);
      if (!v) continue;
      v.p = vec(
        anchor.x + (o.p.x - anchor.x) * factor,
        anchor.y + (o.p.y - anchor.y) * factor,
        anchor.z + (o.p.z - anchor.z) * factor
      );
    }
  }

  pointerDown(e: ToolPointer): void {
    if (e.button !== 0) return;
    if (this.ctx.selection.isEmpty) return;
    this.computeCorners();
    if (this.corners.length === 0) return;
    // Griff unter dem Cursor suchen
    const grips = this.gripScreenPositions();
    let best = -1;
    let bestD = 12;
    for (let i = 0; i < grips.length; i++) {
      const d = Math.hypot(grips[i]!.x - e.x, grips[i]!.y - e.y);
      if (d < bestD) { bestD = d; best = i; }
    }
    if (best < 0) return;
    if (this.originals.length === 0 && !this.captureSelection()) return;
    this.gripIndex = best;
    // Anker: gegenüberliegender Eckpunkt (oder Zentrum mit Strg)
    this.anchorCorner = e.ctrl ? -1 : 7 - best;
    this.anchorPoint = this.anchorCorner === -1 ? this.center : this.corners[this.anchorCorner]!;
    const sa = this.ctx.camera.projectToScreen(this.anchorPoint);
    this.baseLen = Math.hypot(grips[best]!.x - sa.x, grips[best]!.y - sa.y);
    if (this.baseLen < 2) return;
    this.ctx.beginEdit();
    this.active = true;
    this.currentFactor = 1;
    this.ctx.ui.setStatus('Ziehen zum Skalieren · Zahl tippen = Faktor');
  }

  private anchor(): Vec3 {
    return this.anchorPoint ?? (this.anchorCorner === -1 ? this.center : this.corners[this.anchorCorner]!);
  }

  pointerUp(_e: ToolPointer): void {
    if (!this.active) return;
    this.active = false;
    this.anchorPoint = null;
    this.gripIndex = null;
    this.originals = [];
    this.currentFactor = 1;
    this.ctx.ui.setStatus(null);
    this.ctx.preview.clear();
    this.ctx.requestUpdate();
  }

  wantsVcb(): boolean {
    return this.active;
  }

  vcbDisplay(): string | null {
    if (!this.active) return null;
    return `${Math.round(this.currentFactor * 1000) / 1000}`;
  }

  applyVcb(raw: string): void {
    if (!this.active || this.originals.length === 0) return;
    const factor = Number(raw.trim().replace(',', '.').replace(/x$/i, ''));
    if (Number.isFinite(factor) && factor > 0) {
      this.applyScale(factor);
      this.currentFactor = factor;
    }
    this.active = false;
    this.anchorPoint = null;
    this.gripIndex = null;
    this.originals = [];
    this.ctx.preview.clear();
    this.ctx.requestUpdate();
  }

  cancel(): void {
    if (this.active) {
      this.applyScale(1);
      this.active = false;
    }
    this.anchorPoint = null;
    this.gripIndex = null;
    this.originals = [];
    this.ctx.preview.clear();
    this.ctx.ui.setStatus(null);
    this.ctx.requestUpdate();
  }
}
